"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ProtectedRoute } from "@/components/protected-route"
import { Sidebar } from "@/components/sidebar"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { getAdminSettings, setAdminSettings } from "@/lib/admin-config"
import { ArrowLeft, Save, Settings2, Webhook, MessageSquare, FileText } from "@/lib/icons"

export function AdminSettingsPage() {
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [form, setForm] = useState(() => getAdminSettings())

  useEffect(() => {
    setForm(getAdminSettings())
  }, [])

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value ?? "" }))
    setMessage(null)
  }

  const handleSave = () => {
    setSaving(true)
    setMessage(null)
    try {
      setAdminSettings(form)
      setMessage({ type: "success", text: "Configuración guardada correctamente." })
      setTimeout(() => setMessage(null), 4000)
    } catch (e) {
      setMessage({ type: "error", text: "No se pudo guardar." })
    } finally {
      setSaving(false)
    }
  }

  return (
    <ProtectedRoute requireAdmin={true}>
      {({ onLogout }) => (
        <Sidebar onLogout={onLogout}>
          <div className="min-h-full bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950/20 pb-safe">
            <div className="container-fluid mx-auto px-4 py-6 max-w-[800px]">
              <div className="mb-6 flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="default"
                  className="gap-2 rounded-xl"
                  onClick={() => navigate("/admin")}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Volver al panel
                </Button>
              </div>

              <Card className="relative overflow-hidden rounded-2xl border border-white/60 bg-white/80 shadow-xl backdrop-blur-xl dark:border-slate-800/60 dark:bg-slate-900/80">
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-indigo-100/30 dark:to-indigo-900/20" />
                <CardHeader className="relative space-y-1 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15">
                      <Settings2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="font-display text-xl text-slate-900 dark:text-white">
                        Configuración
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground">
                        Variables de entorno y conexión con N8N y Evolution API. Solo visible para administradores.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="relative space-y-6 px-6 pb-6">
                  {/* N8N */}
                  <section className="space-y-3 rounded-xl border border-slate-200/60 bg-white/60 p-4 dark:border-slate-700/60 dark:bg-slate-800/40">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                      <Webhook className="h-4 w-4 text-primary" />
                      N8N
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground">URL del webhook N8N</Label>
                      <Input
                        placeholder="https://tu-n8n.com/webhook/..."
                        value={form.n8nWebhookUrl || ""}
                        onChange={(e) => handleChange("n8nWebhookUrl", e.target.value)}
                        className="rounded-lg border-slate-200 dark:border-slate-600"
                      />
                    </div>
                  </section>

                  {/* Evolution API */}
                  <section className="space-y-3 rounded-xl border border-slate-200/60 bg-white/60 p-4 dark:border-slate-700/60 dark:bg-slate-800/40">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                      <MessageSquare className="h-4 w-4 text-primary" />
                      Evolution API (WhatsApp)
                    </div>
                    <div className="grid gap-4 sm:grid-cols-1">
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground">URL base Evolution API</Label>
                        <Input
                          placeholder="https://tu-evolution-api.com"
                          value={form.evolutionApiUrl || ""}
                          onChange={(e) => handleChange("evolutionApiUrl", e.target.value)}
                          className="rounded-lg border-slate-200 dark:border-slate-600"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground">API Key</Label>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          value={form.evolutionApiKey || ""}
                          onChange={(e) => handleChange("evolutionApiKey", e.target.value)}
                          className="rounded-lg border-slate-200 dark:border-slate-600"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground">Nombre de instancia</Label>
                        <Input
                          placeholder="mi-instancia"
                          value={form.evolutionInstance || ""}
                          onChange={(e) => handleChange("evolutionInstance", e.target.value)}
                          className="rounded-lg border-slate-200 dark:border-slate-600"
                        />
                      </div>
                    </div>
                  </section>

                  {/* Pie y hosting */}
                  <section className="space-y-3 rounded-xl border border-slate-200/60 bg-white/60 p-4 dark:border-slate-700/60 dark:bg-slate-800/40">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                      <FileText className="h-4 w-4 text-primary" />
                      Pie de página y hosting
                    </div>
                    <div className="grid gap-4 sm:grid-cols-1">
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground">Texto del pie (ej. Powered by N8N)</Label>
                        <Input
                          placeholder="Powered by N8N"
                          value={form.footerText || ""}
                          onChange={(e) => handleChange("footerText", e.target.value)}
                          className="rounded-lg border-slate-200 dark:border-slate-600"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground">Etiqueta de hosting (opcional)</Label>
                        <Input
                          placeholder="Vercel · CRM Tepeyac"
                          value={form.hostingLabel || ""}
                          onChange={(e) => handleChange("hostingLabel", e.target.value)}
                          className="rounded-lg border-slate-200 dark:border-slate-600"
                        />
                      </div>
                    </div>
                  </section>

                  {message && (
                    <p
                      className={`rounded-lg px-4 py-2 text-sm font-medium ${message.type === "success"
                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                          : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                        }`}
                    >
                      {message.text}
                    </p>
                  )}

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="gap-2 rounded-xl bg-primary px-6 shadow-md hover:bg-primary/90"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? "Guardando…" : "Guardar configuración"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </Sidebar>
      )}
    </ProtectedRoute>
  )
}

export default AdminSettingsPage
